// Selector de pipeline de un ticket del CRM (POST /tickets/:id/move-pipeline).
//
// Réplica del "Cambiar pipeline" del detalle de ticket del CRM
// (crm/apps/web/src/routes/TicketDetailPage.tsx, 2026-07-16): hasta ese cambio
// Ops movía tickets de pipeline en HubSpot porque el CRM no ofrecía un camino
// legítimo (la validación anti cross-move de /move bloquea stages de otro
// pipeline). Este SPA tenía la misma carencia: el pipeline se pintaba como Pill
// estático y solo el stage era editable (TicketStageSelect).
//
// Semántica del endpoint (tickets.repo.ts del CRM):
//   - Sin `stageId`, el ticket aterriza en el PRIMER stage abierto del pipeline
//     destino (mismo comportamiento que el select del CRM, que no pide stage).
//   - El market del ticket sigue al pipeline destino si éste lo tiene mapeado.
//   - Queda doble rastro en el historial: movimiento de stage (stage_activity_
//     log) + entrada 'pipeline_moved' con { fromPipeline, toPipeline }.
//
// Mismo patrón que TicketStageSelect: valor optimista tras el 204 mientras el
// llamante recarga, revert + motivo visible si el move falla (403 de un rol
// sales incluido), y lista de pipelines cacheada (loadTicketPipelines) para que
// N filas montadas a la vez compartan una única petición.

import { useCallback, useEffect, useMemo, useState } from "react";
import { CrmHttpError, describeCrmError } from "../../api/crm";
import { loadTicketPipelines, moveTicketPipeline } from "../../api/crmTickets";
import { TicketPipeline } from "../../api/crmTypes";
import { useCrmResource } from "../../hooks/useCrmResource";

export default function TicketPipelineSelect({
  ticketId,
  pipelineId,
  pipelineLabel,
  onMoved,
  compact = false,
}: {
  ticketId: string;
  /** Pipeline actual según el servidor. Al cambiar, limpia el valor optimista. */
  pipelineId: string;
  /** Etiqueta del pipeline actual: se pinta mientras cargan las opciones. */
  pipelineLabel: string;
  /** Recargar el recurso del llamante tras un move (204 sin cuerpo). */
  onMoved: () => void;
  /** Variante densa para el panel del lead (filas estrechas del aside). */
  compact?: boolean;
}) {
  const pipelines = useCrmResource<TicketPipeline[]>(
    () => loadTicketPipelines(),
    [],
    "No se pudieron cargar los pipelines",
  );

  // Valor pintado tras un move OK mientras el llamante recarga (el 204 ya
  // confirma el cambio server-side). Se limpia cuando `pipelineId` llega
  // actualizado o si el move falla — mismo contrato que TicketStageSelect.
  const [optimistic, setOptimistic] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setOptimistic(null);
  }, [pipelineId]);

  const onChange = useCallback(
    async (next: string) => {
      if (next === pipelineId || pending) return;
      setPending(true);
      setError(null);
      setOptimistic(next);
      try {
        // Sin stageId: el CRM elige el primer stage abierto del destino.
        await moveTicketPipeline(ticketId, { pipelineId: next });
        onMoved();
      } catch (err) {
        setOptimistic(null);
        setError(
          err instanceof CrmHttpError
            ? err.status === 403
              ? "No tienes permisos para mover tickets de pipeline (requiere rol ops o admin en el CRM)."
              : describeCrmError(err)
            : "Error de conexión con el CRM.",
        );
      } finally {
        setPending(false);
      }
    },
    [ticketId, pipelineId, pending, onMoved],
  );

  // loadTicketPipelines ya filtra archivados y ordena por posición; repetimos
  // el criterio defensivo para no depender de la forma de la respuesta.
  const options = useMemo(
    () =>
      (pipelines.data ?? [])
        .filter((p) => !p.archived)
        .sort((a, b) => a.position - b.position),
    [pipelines.data],
  );

  const value = optimistic ?? pipelineId;
  const loaded = pipelines.data !== null;
  // El pipeline actual puede no estar entre las opciones (archivado con el
  // ticket dentro, o error de carga): sin esta opción de respaldo el <select>
  // pintaría el primer option y mentiría sobre dónde está el ticket.
  const known = loaded && options.some((p) => p.id === value);

  const font = compact ? ".74rem" : ".85rem";

  return (
    <div>
      <select
        value={value}
        disabled={pending || !loaded || !!pipelines.error}
        onChange={(e) => void onChange(e.target.value)}
        aria-label="Cambiar pipeline del ticket"
        title={
          pipelines.error ??
          "Mover el ticket a otro pipeline. Aterriza en el primer stage abierto del destino."
        }
        style={{
          width: "100%",
          padding: compact ? "0.2rem 0.3rem" : "0.3rem 0.4rem",
          borderRadius: "0.4rem",
          border: "1px solid #dee2e6",
          background: pending ? "#f8f9fa" : "white",
          color: "#1f2933",
          fontSize: font,
          cursor: pending || !loaded ? "default" : "pointer",
          opacity: pending ? 0.7 : 1,
        }}
      >
        {!known && (
          <option value={value}>
            {pipelines.loading ? `${pipelineLabel} — cargando…` : pipelineLabel}
          </option>
        )}
        {options.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
      {error && (
        <div
          style={{
            fontSize: ".7rem",
            color: "#b32424",
            marginTop: "0.2rem",
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
